import { motion, useAnimation } from 'framer-motion'
import { Clock } from 'lucide-react'
import { useState } from 'react'

import { useToggleLike } from 'src/features/feeds/api/likes/use-toggle-like'
import { BriefcaseOutline } from 'src/shared/components/icons/referrals/briefcase-outline'
import DotList from 'src/shared/components/ui/dot-list'
import ImageCarousel from 'src/shared/components/ui/miscellaneous/carousel/image-carousel'
import ExternalLinkAlert from 'src/shared/components/ui/popups/external-link/external-link-alert'
import { CommentIcon } from 'src/shared/features/users/assets/comment-icon'
import { FilledHeart } from 'src/shared/features/users/assets/filled-heart'
import type { StreamPost } from 'src/shared/schemas/stream'
import { cn } from 'src/shared/utils/cn'
import { formatMessageTime } from 'src/shared/utils/dates/format-message-time'

import { useMyUserData } from '../../../api/accounts/use-my-user'
import HeartIcon from '../../../assets/heart-icon'
import { professionMap } from '../../../schemas/profession'
import ProfileAvatar from '../../profile-photos/profile-avatar'
import { renderTextWithLinks } from '../util/render-text-with-links'

import { ChatOutlineIcon } from './assets/chat-outline-icon'
import { HeartOutlineIcon } from './assets/heart-outline-icon'
import UserFeedImageGrid from './user-feed-image-grid'

type UserFeedCardProps = {
  post: StreamPost
  // TODO: Remove default when we are sure all the old ui can be removed.
  type?: 'default' | 'new'
}

const UserFeedCard = ({ post, type = 'default' }: UserFeedCardProps) => {
  const author = post.actor
  const hasLiked = post.ownReactions?.like && post.ownReactions.like.length > 0
  const likeReactionId = hasLiked ? post.ownReactions?.like?.[0]?.id : undefined
  const [liked, setLiked] = useState(hasLiked)
  const [likeCount, setLikeCount] = useState(post.reactionCounts?.like || 0)
  const [commentCount] = useState(post.reactionCounts?.comment || 0)
  const likeControls = useAnimation()
  const heartControls = useAnimation()
  const commentControls = useAnimation()
  const toggleLike = useToggleLike()
  const { data: user } = useMyUserData()
  const [externalLinkModalConfig, setExternalLinkModalConfig] = useState({
    isOpen: false,
    externalLink: '',
  })

  if (!author) return null

  const handleLike = async () => {
    if (!user) return

    setLiked((prev) => !prev)
    setLikeCount((prev) => (liked ? prev - 1 : prev + 1))
    // Animate the button container lightly
    likeControls.start({ scale: [1, 1.08, 1] }, { duration: 0.28, ease: 'easeOut' })
    // Animate the heart icon more noticeably
    await heartControls.start({ scale: [1, 1.4, 1] }, { duration: 0.32, ease: 'easeOut' })

    const variables = {
      activityId: post.id,
      parentType: 'post' as const,
      reactionId: hasLiked ? likeReactionId : undefined,
      isLiked: Boolean(hasLiked),
    }

    toggleLike.mutate(variables)
  }

  const handleComment = async () => {
    await commentControls.start({ scale: [1, 1.12, 1] }, { duration: 0.18, ease: 'easeOut' })
  }

  const professionText = author.profession ? professionMap[author.profession]?.label : ''

  const setExternalLinkModalOpen = (isOpen: boolean) => {
    setExternalLinkModalConfig({ isOpen, externalLink: externalLinkModalConfig.externalLink })
  }

  const onPressLink = (url: string) => {
    setExternalLinkModalConfig({ isOpen: true, externalLink: url })
  }

  const text = renderTextWithLinks(post.text, false, onPressLink)

  if (type === 'new') {
    return (
      <div className="flex flex-col gap-[15px]">
        <div className="flex flex-row items-center gap-2">
          <ProfileAvatar
            userId={parseInt(author.id)}
            size={40}
            className="border-2 border-white shadow-[0_1px_6px_1px_rgba(27,48,41,0.15)]"
          />
          <div className="flex flex-1 flex-col">
            <div className="flex flex-1 flex-row items-center justify-between">
              <p className="text-[15px] font-semibold leading-none text-primary">{author.fullName}</p>
              {/* <DotsButton numberOfDots={3} onClick={() => {}} /> */}
            </div>
            <DotList
              dotText="·"
              textClassName="text-[13px] tracking-[-0.26px] leading-[1]"
              items={[professionText, formatMessageTime(post.time, { short: true })]}
            />
          </div>
        </div>
        {post.images.length > 0 && <ImageCarousel images={post.images} className="rounded-[15px]" />}
        <p className="whitespace-pre-wrap text-[14px] leading-[1.65] text-primary">{text}</p>
        <div className="mt-[-3px] flex flex-row items-center gap-5 text-[13px] leading-[-0.13px] text-muted">
          <div className="flex flex-row items-center gap-[5px] hover:cursor-pointer" onClick={handleLike}>
            {liked ? (
              <FilledHeart size={19} className="text-primary" />
            ) : (
              <HeartOutlineIcon size={19} className="text-primary" />
            )}
            {likeCount} Likes
          </div>
          <div className="flex flex-row items-center gap-[5px] hover:cursor-pointer" onClick={handleComment}>
            <ChatOutlineIcon className="text-primary" />
            {commentCount} Comments
          </div>
        </div>
        <ExternalLinkAlert
          isOpen={externalLinkModalConfig.isOpen}
          setOpen={setExternalLinkModalOpen}
          externalLink={externalLinkModalConfig.externalLink}
        />
      </div>
    )
  }

  return (
    <div
      className="relative mb-[15px] h-fit w-full min-w-[280px] max-w-[calc(100vw-40px)] rounded-2xl md:min-w-[325px] md:max-w-none"
      style={{
        boxShadow: '0px 3px 10px 1px rgba(220, 219, 218, 0.25)',
      }}
    >
      <div className="overflow-hidden rounded-2xl border border-[#efefef] bg-white">
        <div className={cn('flex flex-col p-4', post.images.length === 0 && 'mb-2')}>
          <div className="flex items-start justify-between">
            <div className={cn('flex items-center gap-3')}>
              <div>
                <ProfileAvatar userId={parseInt(author.id)} size={35} />
              </div>
              <div className="flex flex-col gap-0.5">
                <div className="flex items-center gap-1 text-base">
                  <span className={cn('text-[13px] font-bold leading-tight text-primary')}>{author.fullName}</span>

                  <span className={cn('flex items-center truncate text-[13px] leading-tight text-muted')}>
                    @{author.username}
                  </span>
                </div>
                <div className="flex items-center gap-0.5">
                  {author.profession && (
                    <div className="flex items-center gap-1">
                      <BriefcaseOutline className={cn('h-5 w-[21.433px] text-muted')} />
                      <p className="text-[12px] leading-tight">{professionMap[author.profession]?.label}</p>
                    </div>
                  )}
                  {author.shortAddress && (
                    <div className="flex items-center gap-0.5 text-[12px] leading-tight text-muted">
                      <span>·</span>
                      <span>{author.shortAddress}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className={cn('flex items-start gap-1')}>
              {post.time && (
                <div className="flex flex-row items-center text-xs text-muted">
                  <Clock className="mr-1 size-2.5" />
                  <span>{formatMessageTime(post.time, { short: true })}</span>
                </div>
              )}
            </div>
          </div>
          <div className="mt-3 text-[13px] text-primary">
            {post.text ||
              "Closed a deal today where the client didn't know their real score until months in. A little guidance goes a long way—feels good to help people get their first home."}
          </div>
        </div>
        <UserFeedImageGrid images={post.images} maxImages={5} />
        {/* Reactions bar */}
        <div className="absolute bottom-[-15px] left-4 flex flex-row gap-2">
          <motion.button
            className={`flex h-[30px] items-center gap-1 rounded-xl border bg-[#f8f7f5] px-3 text-[13px] font-bold ${
              liked ? 'border-primary bg-primary text-white' : 'border-[#f0f0f0] text-primary'
            }`}
            animate={likeControls}
            whileTap={{ scale: 0.97 }}
            onClick={handleLike}
            style={{ outline: 'none', boxShadow: '0px 2px 6px 1px rgba(27, 48, 41, 0.10)' }}
          >
            <motion.span animate={heartControls} style={{ display: 'flex', alignItems: 'center' }}>
              {liked ? <FilledHeart size={14} /> : <HeartIcon size={14} />}
            </motion.span>
            <span className={liked ? 'text-white' : 'text-primary'}>{likeCount}</span>
          </motion.button>
          <motion.button
            className="flex h-[30px] items-center gap-1 rounded-xl border border-[#f0f0f0] bg-[#f8f7f5] px-3 text-[13px] font-bold text-primary shadow"
            animate={commentControls}
            whileTap={{ scale: 0.97 }}
            onClick={handleComment}
            style={{ outline: 'none', boxShadow: '0px 2px 6px 1px rgba(27, 48, 41, 0.10)' }}
          >
            <CommentIcon size={14} />
            <span>{commentCount}</span>
          </motion.button>
        </div>
      </div>
    </div>
  )
}

export default UserFeedCard
